import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import TitleCard from "@/components/feature/TitleCard";
import { titles } from "@/mocks/titles";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";

async function copyText(text: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = titles.find((entry) => entry.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [openEpisode, setOpenEpisode] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const [shareState, setShareState] = useState<"idle" | "success" | "error">("idle");

  usePageMeta(
    item ? `${item.title}-奇优影院详细介绍` : "内容不存在-奇优影院",
    item
      ? `${item.title}（${item.year}·${item.region}）：${item.synopsis.slice(0, 70)}…`
      : "未找到对应影视作品，返回首页或分类页浏览其它内容。",
    item ? `${item.title},${item.genres.join(",")},奇优影院` : "奇优影院",
  );

  useEffect(() => {
    setOpenEpisode(null);
    setShareState("idle");
  }, [id]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!item) return [];
    return titles
      .filter((entry) => entry.id !== item.id && entry.type === item.type)
      .map((entry) => ({
        entry,
        score:
          entry.genres.filter((genre) => item.genres.includes(genre)).length * 2 +
          (entry.region === item.region ? 1 : 0) +
          (entry.year === item.year ? 1 : 0),
      }))
      .sort((a, b) => b.score - a.score || b.entry.hot - a.entry.hot)
      .slice(0, 4)
      .map((row) => row.entry);
  }, [item]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-movie-2-line text-3xl" />
          </span>
          <h1 className="text-xl md:text-2xl font-extrabold text-foreground-950 font-heading mt-4">内容不存在</h1>
          <p className="text-sm text-foreground-600 mt-2">
            该影视作品可能已被移除，或链接中的编号有误。可以返回首页重新查找。
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <Link
              to="/"
              className="h-10 px-4 inline-flex items-center rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
            <Link
              to="/category/movie"
              className="h-10 px-4 inline-flex items-center rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览电影
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const favorited = isFavorite(item.id);
  const typePath = item.type === "movie" ? "/category/movie" : "/category/tv";
  const typeLabel = item.type === "movie" ? "电影" : "电视剧";

  const handleFavorite = () => {
    const next = toggleFavorite(item.id);
    setToast(next ? "已加入收藏（本机演示）" : "已取消收藏");
  };

  const handleShare = async () => {
    const ok = await copyText(window.location.href);
    setShareState(ok ? "success" : "error");
  };

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-5 md:pt-7">
        <Breadcrumbs
          items={[{ label: "首页", to: "/" }, { label: typeLabel, to: typePath }, { label: item.title }]}
        />

        <header className="rounded-lg bg-primary-950 px-4 py-5 md:px-7 md:py-7">
          <div className="flex flex-col sm:flex-row gap-5 md:gap-7">
            <div className="w-[150px] md:w-[196px] shrink-0 aspect-[3/4] rounded-lg overflow-hidden bg-primary-900 mx-auto sm:mx-0">
              <img
                src={item.poster}
                alt={`${item.title}海报`}
                title={`${item.title} ${item.genres[0]} 奇优影院`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2 py-0.5 rounded bg-accent-500 text-background-50 text-[11px] font-label">
                  {typeLabel}
                </span>
                <span className="px-2 py-0.5 rounded bg-primary-800 text-secondary-300 text-[11px] font-label">
                  热度 {item.hot}
                </span>
                {item.episodeNote && (
                  <span className="text-[11px] text-background-400 font-label">{item.episodeNote}</span>
                )}
              </div>

              <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 font-heading mt-2.5 leading-tight">
                {item.title}
              </h1>
              <p className="text-sm text-background-300/90 mt-2 leading-relaxed max-w-2xl line-clamp-2">
                {item.synopsis}
              </p>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-4 text-sm">
                <div className="flex gap-2">
                  <dt className="text-background-400 shrink-0">导演</dt>
                  <dd className="text-background-100 truncate">{item.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-400 shrink-0">主演</dt>
                  <dd className="text-background-100 truncate">{item.cast.join(" / ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-400 shrink-0">类型</dt>
                  <dd className="text-background-100">{item.genres.join(" · ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-400 shrink-0">地区 / 年份</dt>
                  <dd className="text-background-100">
                    {item.region} · {item.year}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-400 shrink-0">{item.type === "movie" ? "片长" : "集数"}</dt>
                  <dd className="text-background-100">
                    {item.type === "movie" ? item.duration : `全 ${item.episodeCount} 集`}
                  </dd>
                </div>
              </dl>

              <div className="flex flex-wrap items-center gap-2 mt-5">
                <button
                  type="button"
                  onClick={handleFavorite}
                  aria-pressed={favorited}
                  className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-secondary-400 text-foreground-950 hover:bg-secondary-500"
                      : "bg-accent-500 text-background-50 hover:bg-accent-600"
                  }`}
                >
                  <i
                    className={`${favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base w-4 h-4 flex items-center justify-center`}
                  />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-primary-700 text-sm text-background-100 hover:border-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-link-m w-4 h-4 flex items-center justify-center" />
                  复制链接
                </button>
                {shareState === "success" && (
                  <span className="text-xs text-secondary-400" role="status">
                    链接已复制到剪贴板
                  </span>
                )}
                {shareState === "error" && (
                  <span className="text-xs text-accent-300" role="status">
                    复制失败，请手动复制浏览器地址栏
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5 md:gap-6 items-start mt-5">
          <div className="space-y-4">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-2.5">剧情简介</h2>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-[1.9]">{item.synopsis}</p>
              <div className="mt-4 rounded-md bg-secondary-50 border-l-2 border-secondary-400 px-3.5 py-3">
                <p className="text-xs tracking-[0.14em] text-secondary-900 font-label uppercase mb-1">看点</p>
                <p className="text-sm text-foreground-700 leading-relaxed">{item.highlights}</p>
              </div>
            </section>

            {item.type === "tv" && item.episodes && item.episodes.length > 0 && (
              <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-foreground-950 font-heading">剧集目录</h2>
                  <span className="text-xs text-foreground-500">共 {item.episodeCount} 集 · 展示前 {item.episodes.length} 集</span>
                </div>
                <div className="space-y-2">
                  {item.episodes.map((episode, index) => {
                    const expanded = openEpisode === episode.id;
                    return (
                      <div key={episode.id} className="rounded-md border border-background-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setOpenEpisode(expanded ? null : episode.id)}
                          aria-expanded={expanded}
                          className="w-full flex items-center justify-between gap-3 px-3 py-3 min-h-11 text-left bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span className="flex items-center gap-2.5 min-w-0">
                            <span className="w-6 h-6 rounded bg-background-100 text-[11px] text-foreground-500 flex items-center justify-center shrink-0 font-label">
                              {index + 1}
                            </span>
                            <span className="text-sm text-foreground-900 truncate">
                              {episode.title.replace(/^第\d+集\s*/, "")}
                            </span>
                          </span>
                          <i
                            className={`${expanded ? "ri-subtract-line" : "ri-add-line"} text-base w-4 h-4 flex items-center justify-center text-foreground-400 shrink-0`}
                          />
                        </button>
                        {expanded && (
                          <p className="px-3 pb-3 pt-0 text-xs md:text-sm text-foreground-600 leading-relaxed bg-background-50 animate-fade-in">
                            {episode.summary}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground-950 font-heading">同类推荐</h2>
                <Link
                  to={typePath}
                  className="text-xs text-primary-700 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  更多{typeLabel}
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {related.map((entry) => (
                  <TitleCard key={entry.id} item={entry} compact />
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-20">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-bold text-foreground-950 font-heading mb-3">作品信息</h2>
              <dl className="space-y-2 text-sm">
                {[
                  { label: "作品类型", value: typeLabel },
                  { label: "上映年份", value: String(item.year) },
                  { label: "制片地区", value: item.region },
                  { label: "导演", value: item.director },
                  { label: "主演", value: item.cast.join(" / ") },
                  { label: "时长", value: item.type === "movie" ? item.duration ?? "-" : `${item.episodeCount} 集` },
                ].map((row) => (
                  <div key={row.label} className="flex gap-2">
                    <dt className="text-foreground-500 shrink-0 w-20">{row.label}</dt>
                    <dd className="text-foreground-800 min-w-0 flex-1">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-background-100">
                {item.genres.map((genre) => (
                  <span
                    key={genre}
                    className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900 text-[11px] font-label"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-bold text-foreground-950 font-heading mb-2">收藏与分享</h2>
              <p className="text-xs text-foreground-600 leading-relaxed">
                收藏状态保存在本机浏览器，换设备后不会同步；“复制链接”仅生成当前详情页地址，不调用任何第三方分享服务。
              </p>
              <div className="flex flex-col gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className="h-9 rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {favorited ? "取消收藏" : "加入收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="h-9 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  复制本页链接
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-4">
              <p className="text-xs text-foreground-600 leading-relaxed">
                本站内容为虚构示例，不提供观看、下载或票务服务。演职人员姓名与剧情均为创作内容。
              </p>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div
          role="status"
          className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm animate-fade-up"
        >
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}